import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getPlayerById } from '../../../features/playerProfile/playerProfileSlice';
import { normalizePlayerClass } from '../../../utils/normalizePlayerClass';
import { DashboardNavbar } from '../../../components/layout/DashboardNavbar';
import { VideoPlayerModal } from '../../../components/VideoPlayer/VideoPlayerModal';
import { VideoCarousel } from '../../../components/VideoPlayer/VideoCarousel';
import { ContactDetails, TeamLogo } from '../../dashboard/profile/PlayerProfileHeader';
import { StatisticsSection } from '../../dashboard/profile/StatisticsSection';
import { CARD, CardTitle, InfoGrid, BulletList } from '../../dashboard/profile/profileCard';

// Another player's profile, opened by a signed-in player from a team roster.
// Same look as the player's own profile, but read-only: no editing, no
// progress strip, and only the player's public socials (no phone or email).

const PlayerPhoto = ({ name, src }) => {
  const [failed, setFailed] = useState(false);
  const frame = 'w-32 h-32 sm:w-44 sm:h-44 flex-shrink-0 rounded-2xl border-2 border-cyan-400/60 overflow-hidden';

  if (src && !failed) {
    return <img src={src} alt={name} onError={() => setFailed(true)} className={`${frame} object-cover bg-white/10`} />;
  }

  return (
    <div className={`${frame} bg-white/10 flex items-end justify-center`} role="img" aria-label={`${name} photo`}>
      <svg className="w-4/5 h-4/5 text-white/40" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-4.42 0-8 2.69-8 6v2h16v-2c0-3.31-3.58-6-8-6z" />
      </svg>
    </div>
  );
};

const Header = ({ player, name }) => {
  const navigate = useNavigate();
  const teamName = typeof player.team === 'object' ? player.team?.name : '';
  const subtitle = [player.primaryPosition || player.position, teamName].filter(Boolean).join(' · ');

  return (
    <section className="bg-jp-band px-4 sm:px-6 lg:px-8 pt-28 sm:pt-32 pb-10">
      <div className="max-w-7xl mx-auto">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-6 inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 rounded"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div className="flex items-center gap-5 min-w-0">
            <PlayerPhoto name={name} src={player.profileImage} />
            <div className="min-w-0">
              <p className="text-yellow-300 text-sm font-bold uppercase tracking-widest mb-2">Player Profile</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-anton leading-[1.1] tracking-tight text-white uppercase">
                {name}
              </h1>
              {/* Position and school, with the jersey number to the right of the school */}
              {(subtitle || player.jerseyNumber) && (
                <p className="text-slate-300 text-sm sm:text-base mt-2">
                  {subtitle}
                  {player.jerseyNumber && (
                    <span className="ml-2 text-white font-bold">#{player.jerseyNumber}</span>
                  )}
                </p>
              )}
              <ContactDetails profile={player} hidePrivate />
            </div>
          </div>
          <TeamLogo team={player.team} />
        </div>
      </div>
    </section>
  );
};

const VideoCard = ({ player }) => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const videos = player.videos || [];

  return (
    <section className={CARD}>
      <CardTitle title="Video" />
      {videos.length > 0 ? (
        <VideoCarousel videos={videos} onVideoClick={setSelectedVideo} isEditing={false} showUploadSlots={false} maxVideos={6} />
      ) : (
        <p className="text-jp-muted/80 text-sm">No videos yet.</p>
      )}
      <VideoPlayerModal
        isOpen={Boolean(selectedVideo)}
        onClose={() => setSelectedVideo(null)}
        video={selectedVideo}
        profile={player}
        allVideos={videos}
      />
    </section>
  );
};

export const PlayerProfileView = () => {
  const { playerId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const { viewedPlayer, viewedPlayerLoading, viewedPlayerError } = useSelector((state) => state.playerProfile);

  // Check if player data passed via navigation state
  const [playerFromState] = useState(location.state?.playerData || null);

  // Fetch player data only if not available in state
  useEffect(() => {
    if (!playerFromState && playerId) {
      dispatch(getPlayerById(playerId));
    }
  }, [dispatch, playerId, playerFromState]);

  // Use data from state if available, otherwise use fetched data
  const currentPlayer = playerFromState || viewedPlayer;

  // Roster rows carry one season under stats.*; the profile API has arrays
  const player = currentPlayer && {
    ...currentPlayer,
    battingStats: currentPlayer.stats?.battingStats ? [currentPlayer.stats.battingStats] : currentPlayer.battingStats || [],
    pitchingStats: currentPlayer.stats?.pitchingStats ? [currentPlayer.stats.pitchingStats] : currentPlayer.pitchingStats || [],
    fieldingStats: currentPlayer.stats?.fieldingStats ? [currentPlayer.stats.fieldingStats] : currentPlayer.fieldingStats || [],
  };

  const name = player
    ? `${player.firstName || ''} ${player.lastName || ''}`.trim() || player.name || '-'
    : '';
  const weight = player?.weight ? (typeof player.weight === 'number' ? `${player.weight} LBS` : player.weight) : '';
  const transferStatus = Array.isArray(player?.transferStatus) ? player.transferStatus.join(', ') : player?.transferStatus;
  const commitment = player?.commitmentStatus
    ? String(player.commitmentStatus).toLowerCase() === 'committed' ? 'Committed' : 'Uncommitted'
    : '';
  const previousTeams = player?.previousTeams
    ? String(player.previousTeams).split(',').map((t) => t.trim()).filter(Boolean)
    : [];
  const transcript = player?.acedemicInfo || player?.academicInfo;
  const transcriptUrl = transcript?.url || transcript?.documentUrl;

  return (
    <div className="min-h-screen bg-jp-page text-jp-ink text-left overflow-x-hidden">
      <DashboardNavbar userRole="player" />

      {/* Loading State - only show when fetching from API */}
      {!player && viewedPlayerLoading && (
        <div className="flex items-center justify-center pt-40 pb-12">
          <div className="w-12 h-12 border-4 border-jp-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {/* Error State */}
      {!player && viewedPlayerError && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32">
          <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/40 text-red-600 dark:text-red-400 text-sm">
            {viewedPlayerError}
          </div>
        </div>
      )}

      {player && (
        <>
          <Header player={player} name={name} />

          {/* Same card order as the player's own profile */}
          <main className="px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
              <section className={`${CARD} lg:col-span-2 min-w-0`}>
                <CardTitle title="Player Information" />
                <InfoGrid
                  items={[
                    { label: 'Position', value: player.primaryPosition || player.position },
                    { label: 'College', value: player.previousSchool },
                    { label: 'Class', value: normalizePlayerClass(player.playerClass || player.class) },
                    { label: 'B/T', value: player.batsThrows || player.batThrow },
                    { label: 'Height', value: player.height },
                    { label: 'Weight', value: weight },
                    { label: 'Hometown', value: player.hometown },
                    { label: 'High School', value: player.highSchool },
                    { label: 'Commitment', value: commitment },
                    { label: 'Transfer Status', value: transferStatus },
                  ]}
                />
              </section>

              <div className="lg:col-span-2 min-w-0">
                <StatisticsSection player={player} />
              </div>

              <div className="lg:col-span-2 min-w-0">
                <VideoCard player={player} />
              </div>

              <section className={CARD}>
                <CardTitle title="Bio" />
                <p className="text-jp-muted text-sm leading-relaxed whitespace-pre-line">
                  {player.description || player.playerBio || player.bio || 'No bio yet.'}
                </p>
              </section>

              <section className={CARD}>
                <CardTitle title="Awards & Achievements" />
                <BulletList items={player.awardsAchievements || []} empty="No awards listed yet." />
              </section>

              <section className={`${CARD} lg:col-span-2`}>
                <CardTitle title="Previous Teams" />
                <BulletList items={previousTeams} empty="No previous teams listed." />
              </section>

              <section className={`${CARD} lg:col-span-2`}>
                <CardTitle title="Academic Information">
                  {transcriptUrl && (
                    <a
                      href={transcriptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-semibold text-jp-accent hover:underline underline-offset-4"
                    >
                      View transcript
                    </a>
                  )}
                </CardTitle>
                <InfoGrid
                  items={[
                    { label: 'GPA', value: player.academic_info_gpa },
                    { label: 'SAT Score', value: player.academic_info_sat },
                    { label: 'ACT Score', value: player.academic_info_act },
                  ]}
                />
              </section>
            </div>
          </main>
        </>
      )}
    </div>
  );
};

export default PlayerProfileView;
